import { AnimatePresence, motion } from 'framer-motion';
import { type FC, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { PillTabsProps } from '@/types';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import useChainStore from '@/store/useChainStore';
import {
  SEARCH_PARAM_INCIDENT_ID,
  SEARCH_PARAM_INCIDENTS,
  SEARCH_PARAM_JOURNEY_ID,
  SEARCH_PARAM_LOCK_PIN,
  SEARCH_PARAM_LOCK_PIN_TYPE,
  SEARCH_PARAM_MONITOR,
  SEARCH_PARAM_ROUTE_LINE_NUMBER,
  SEARCH_PARAM_ROUTE_TYPE,
  SEARCH_PARAM_ROUTES,
  SEARCH_PARAM_SECTION_ID,
  SEARCH_PARAM_SELECTED_DATE,
  SEARCH_PARAM_SHOW,
  SEARCH_PARAM_STATION_ID,
  SEARCH_PARAM_STATIONS,
  SEARCH_PARAM_VIEW,
  SEARCH_PARAMS_ROUTE_ID,
  VIEW_LADDER,
} from '@/utils/constants/queries';

const PillTabs: FC<PillTabsProps> = ({ tabs }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const shouldHandleTabChangeRef = useRef<string>('');
  const { clearChain } = useChainStore();
  const isLadderView = searchParams.get(SEARCH_PARAM_VIEW) === VIEW_LADDER;
  const sectionId = searchParams.get(SEARCH_PARAM_SECTION_ID);
  const selectedTab = searchParams?.get(SEARCH_PARAM_SHOW) ?? '';
  const lockType = searchParams?.get(SEARCH_PARAM_LOCK_PIN_TYPE);
  const lockPin = searchParams?.get(SEARCH_PARAM_LOCK_PIN);
  // runs asynchronously (`onExitComplete`). Without it, the callback may
  // capture a stale closure, leading to incorrect URL param cleanup and
  // transition issues when switching tabs from detail views. Including it
  // does not trigger re-renders and ensures predictable behavior.

  const handleTabChange = useCallback(
    (path: string) => {
      shouldHandleTabChangeRef.current = '';

      clearChain();

      setSearchParams(() => {
        const newParams = new URLSearchParams();

        newParams.set(SEARCH_PARAM_SHOW, path);

        if (isLadderView) {
          newParams.set(SEARCH_PARAM_VIEW, VIEW_LADDER);

          if (sectionId) {
            newParams.set(SEARCH_PARAM_SECTION_ID, sectionId);
          }
        }

        if (lockType && lockPin) {
          newParams.set(SEARCH_PARAM_LOCK_PIN_TYPE, lockType);
          newParams.set(SEARCH_PARAM_LOCK_PIN, lockPin);
        }

        return newParams;
      });
    },
    [isLadderView, sectionId, lockType, lockPin]
  );

  const handleAnimationComplete = useCallback(() => {
    if (shouldHandleTabChangeRef.current === selectedTab) {
      return;
    }

    shouldHandleTabChangeRef.current = selectedTab;

    setSearchParams((prev) => {
      if (selectedTab !== SEARCH_PARAM_ROUTES) {
        prev.delete(SEARCH_PARAMS_ROUTE_ID);
        prev.delete(SEARCH_PARAM_JOURNEY_ID);
        prev.delete(SEARCH_PARAM_ROUTE_LINE_NUMBER);
      }

      if (selectedTab !== SEARCH_PARAM_STATIONS) {
        prev.delete(SEARCH_PARAM_STATION_ID);
      }

      if (selectedTab !== SEARCH_PARAM_INCIDENTS) {
        prev.delete(SEARCH_PARAM_MONITOR);
        prev.delete(SEARCH_PARAM_SELECTED_DATE);
        prev.delete(SEARCH_PARAM_ROUTE_TYPE);
        prev.delete(SEARCH_PARAM_INCIDENT_ID);
        prev.delete(SEARCH_PARAM_INCIDENTS);
      }

      return prev;
    });
  }, [selectedTab]);

  return (
    <Tabs
      value={selectedTab}
      onValueChange={handleTabChange}
      className="block w-full px-2 py-1 max-h-13"
    >
      <TabsList className="p-0 h-10 bg-background gap-10 flex justify-center w-full">
        {tabs.map(({ key, label }) => (
          <TabsTrigger
            key={key}
            value={key}
            className="max-w-17 min-w-17 h-10 rounded-full cursor-pointer fill-muted-foreground data-[state=active]:bg-primary-subtle data-[state=active]:fill-primary"
            aria-label={key}
          >
            <div className="text-[13px]">{label}</div>
          </TabsTrigger>
        ))}
      </TabsList>
      <div className="relative min-h-[100px] mt-4">
        <AnimatePresence mode="wait" onExitComplete={handleAnimationComplete}>
          {tabs.map(
            ({ key, content }) =>
              key === selectedTab && (
                <TabsContent key={key} value={key} forceMount>
                  <motion.div
                    key={key}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute w-full"
                  >
                    {content}
                  </motion.div>
                </TabsContent>
              )
          )}
        </AnimatePresence>
      </div>
    </Tabs>
  );
};

export default PillTabs;
